/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react';
import { CommandBar as FabricCommandBar, ICommandBarItemProps, ICommandBarProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type LuisCommandBarProps = ICommandBarProps & {
    /**
     * A flag to whether remove the extra padding
     * on the left in the command bar.
     */
    removeDefaultPadding?: boolean;

    /**
     * Whether to add a top border the command bar or not.
     * @default true
     */
    showTopBorder?: boolean;

    /**
     * Include a search bar in the far items
     * in the search bar.
     */
    includeSearchBar?: boolean;

    /**
     * Indicate whether the search bar (if existed) always expanded or not.
     */
    searchBarAlwaysExpanded?: boolean;
    /**
     * The query to display in the search bar. Useful
     * only if includeSearchBar is true.
     */
    query?: string;

    /**
     * Handler when query is changed. Useful
     * only if includeSearchBar is true.
     */
    onQueryChange?: (query: string) => void;

    /**
     * [Optional] Specifies the styled border position.
     * default value: 'top'.
     */
    borderPosition?: 'Bottom' | 'Top';

    hideBorder?: boolean;
};

const messages = defineMessages({
    searchPlaceholder: { id: 'core.commandBar.searchPlaceholder', defaultMessage: 'Search' },
    overflowButtonAriaLabel: { id: 'core.commandBar.overflowButtonAriaLabel', defaultMessage: 'More' },
    hintAriaLabel: { id: 'core.commandBar.hintAriaLabel', defaultMessage: 'Use left and right arrow keys to navigate between commands' }
});

const commandBarTheme = getScopedTheme('LuisCommandBar');
const noPaddingCommandBarStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({ root: { paddingLeft: 0, paddingRight: 0 } });

/**
 * A thin wrapper around Fabric's command bar
 * component that provides default styling and
 * accessibility labels.
 */
export const LuisCommandBar = (props: LuisCommandBarProps) => {
    const {
        query,
        items = [],
        farItems = [],
        onQueryChange,
        hideBorder = false,
        showTopBorder = true,
        borderPosition = 'Bottom',
        includeSearchBar = false,
        removeDefaultPadding = true,
        searchBarAlwaysExpanded = false
    } = props;

    const { localize } = useLocalization();
    const { isSmallOrGreater, isMediumOrGreater } = useResponsiveMode();
    const commandBarId = React.useRef<string>(getId());

    const borderCommandBarStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({
        root: { [`border${borderPosition}`]: `${hideBorder ? null : `1px solid ${commandBarTheme.borderColor}`}` }
    });

    const mergedStyles = React.useMemo(() => {
        const paddingStyles = removeDefaultPadding && noPaddingCommandBarStyles;
        const propStyles = props.styles;
        const borderStyles = showTopBorder && borderCommandBarStyles;
        const displayStyles = { primarySet: { flexWrap: 'wrap' } };

        return mergeStyleSets(paddingStyles, borderStyles, propStyles, displayStyles);
    }, [removeDefaultPadding, showTopBorder, props.styles]);

    const getFarItems = React.useCallback(() => {
        if (!includeSearchBar) {
            return farItems;
        }

        return [
            {
                key: 'search',
                iconOnly: true,
                iconProps: { iconName: 'Search' },
                onRender: () => (
                    <Stack role="menuitem">
                        <LuisDebouncedCollapsableSearchBox
                            value={query}
                            alwaysExpanded={searchBarAlwaysExpanded}
                            disableAnimation={!searchBarAlwaysExpanded}
                            onChange={(_, value) => onQueryChange?.(value)}
                            ariaLabel={localize(messages.searchPlaceholder)}
                            placeholder={localize(messages.searchPlaceholder)}
                        />
                    </Stack>
                )
            },
            ...(farItems ?? [])
        ];
    }, [query, farItems, onQueryChange, includeSearchBar]);

    const commandBarItems = React.useMemo(() => items.concat(...(isSmallOrGreater && !isMediumOrGreater ? getFarItems() : [])), [
        items,
        getFarItems,
        isSmallOrGreater,
        isMediumOrGreater
    ]);

    const commandBarFarItems = React.useMemo((): ICommandBarItemProps[] => {
        if (isSmallOrGreater && !isMediumOrGreater) {
            return [];
        }

        return getFarItems();
    }, [query, getFarItems, isSmallOrGreater, isMediumOrGreater]);

    return (
        <FabricCommandBar
            componentRef={ref =>
                ref && document.querySelector(`[aria-label="${localize(messages.hintAriaLabel)}"]`)?.setAttribute('role', 'none')
            }
            id={commandBarId.current}
            ariaLabel={localize(messages.hintAriaLabel)}
            overflowButtonProps={{ ariaLabel: localize(messages.overflowButtonAriaLabel) }}
            {...props}
            styles={mergedStyles}
            items={commandBarItems}
            farItems={commandBarFarItems}
        />
    );
};
